import PropTypes from 'prop-types';
import { paceChip } from './familyPlanView';
import '../progress/ProgressSummary.css';
import './FamilyPlanSummary.css';

// React 19 ignores defaultProps on function components, so defaults live here.
const FamilyPlanSummary = ({ hero, rows = [], onEdit = null }) => {
  if (hero.empty) {
    return (
      <section className="family-plan" aria-label="Family plan">
        <p className="family-plan-empty">No family plan yet.</p>
        {onEdit ? (
          <button type="button" className="family-text-button" onClick={onEdit}>
            Set a plan
          </button>
        ) : (
          <p className="family-plan-note">The owner sets this plan.</p>
        )}
      </section>
    );
  }

  return (
    <section className="family-plan progress-summary" aria-label="Family plan">
      <div className="progress-summary-hero">
        <p className="progress-summary-percent">
          <strong>{hero.percent}%</strong>
          {' '}
          of the plan
        </p>
        <div
          className="progress-summary-bar"
          role="progressbar"
          aria-valuenow={hero.percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Percent of the family plan read"
        >
          <span style={{ width: `${hero.percent}%` }} />
        </div>
        <p className="progress-summary-caption">
          {hero.read.toLocaleString()}
          {' of '}
          {hero.total.toLocaleString()}
          {' chapters · '}
          {hero.caption}
        </p>
        {hero.missedDays && (
          <p className="progress-summary-caption">Missed days are included.</p>
        )}
      </div>

      <ul className="progress-summary-chips">
        <li className="progress-chip">
          <span className="progress-chip-value">{hero.read.toLocaleString()}</span>
          <span className="progress-chip-label">chapters</span>
        </li>
        {hero.pace && (
          <li className="progress-chip">
            <span className="progress-chip-value">{hero.pace.value}</span>
            <span className="progress-chip-label">{hero.pace.label}</span>
          </li>
        )}
        {hero.daysLeft != null && (
          <li className="progress-chip">
            <span className="progress-chip-value">{hero.daysLeft <= 0 ? 'Last' : hero.daysLeft}</span>
            <span className="progress-chip-label">{hero.daysLeft === 1 ? 'day left' : 'days left'}</span>
          </li>
        )}
      </ul>

      {rows.length > 0 && (
        <ul className="family-plan-rows">
          {rows.map((row) => {
            const total = row.total || 0;
            const read = row.read || 0;
            const percent = total > 0 ? Math.round((Math.min(read, total) / total) * 100) : 0;
            const pace = paceChip(row.delta);
            return (
              <li key={row.readerId} className="family-plan-row">
                <span className="family-plan-name">{row.name}</span>
                <span className="family-plan-track" aria-hidden="true">
                  <span style={{ width: `${percent}%` }} />
                </span>
                <span className="family-plan-pace">{pace ? pace.value : ''}</span>
              </li>
            );
          })}
        </ul>
      )}

      {onEdit && (
        <button type="button" className="family-text-button" onClick={onEdit}>
          Change plan
        </button>
      )}
    </section>
  );
};

FamilyPlanSummary.propTypes = {
  hero: PropTypes.shape({
    empty: PropTypes.bool,
    percent: PropTypes.number,
    read: PropTypes.number,
    total: PropTypes.number,
    caption: PropTypes.string,
    missedDays: PropTypes.bool,
    pace: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    }),
    daysLeft: PropTypes.number,
    daysLabel: PropTypes.string
  }).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({
    readerId: PropTypes.string,
    name: PropTypes.string,
    read: PropTypes.number,
    total: PropTypes.number,
    delta: PropTypes.number
  })),
  onEdit: PropTypes.func
};

export default FamilyPlanSummary;
